import React, { useEffect, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";

export interface FilterOption<T extends string> {
  value: T;
  label: string;
}

interface FilterDropdownProps<T extends string> {
  /** What the control filters, shown while the default option is selected. */
  placeholder: string;
  options: FilterOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /**
   * The option that means "no filter". While it is selected the trigger shows
   * the placeholder instead of its label, so the control reads as a filter
   * waiting to be used rather than as one already applied.
   */
  defaultValue: T;
  /** Layout only — the width the control takes in its row. */
  className?: string;
}

/**
 * A short list of mutually exclusive filters, in a box.
 *
 * Written here rather than reached for from the dietitian app, whose dropdown
 * carries search, creation, remote paging and a pinned footer row — six
 * hundred lines to offer five fixed choices. What that one has and this does
 * not is worth having when a list is long or unknown; a plan filter is neither.
 *
 * It is a real `listbox`: the trigger says what is selected and how many
 * choices there are, Escape closes it, and a click anywhere outside dismisses
 * it. What it deliberately does not do is portal itself — the panel is
 * absolutely positioned under the trigger, which is correct as long as it sits
 * in a card that does not clip it. It sits in the users card's header, which
 * does not.
 */
function FilterDropdown<T extends string>({
  placeholder,
  options,
  value,
  onChange,
  defaultValue,
  className = "",
}: FilterDropdownProps<T>) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Closes on a click anywhere that is not the control. Listening on the
  // document rather than on a backdrop element, so nothing invisible is laid
  // over the page while the list happens to be open.
  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const selected = options.find((option) => option.value === value);
  const isFiltered = value !== defaultValue;

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`w-full flex items-center justify-between gap-2 rounded-md border bg-white py-2 pl-3 pr-2.5 text-[14px] transition-colors ${
          // A filter that is doing something says so at rest, so nobody has to
          // open the list to find out why the table is short.
          isFiltered
            ? "border-brand text-ink font-medium"
            : "border-line text-ink hover:border-ink-subtle"
        }`}
      >
        <span className="truncate">
          {isFiltered ? selected?.label ?? placeholder : placeholder}
        </span>
        <ChevronDown
          aria-hidden="true"
          className={`w-4 h-4 flex-shrink-0 text-ink-subtle transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {open && (
        <ul
          role="listbox"
          aria-label={placeholder}
          className="absolute right-0 z-20 mt-1 w-full min-w-[180px] overflow-hidden rounded-md border border-line bg-white py-1 shadow-[0_8px_24px_rgba(15,39,51,0.12)]"
        >
          {options.map((option) => {
            const active = option.value === value;

            return (
              <li key={option.value}>
                <button
                  type="button"
                  role="option"
                  aria-selected={active}
                  onClick={() => {
                    onChange(option.value);
                    setOpen(false);
                  }}
                  className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-[14px] transition-colors ${
                    active
                      ? "bg-brand-soft text-brand-dark font-medium"
                      : "text-ink hover:bg-[#F4F7F8]"
                  }`}
                >
                  <span className="truncate">{option.label}</span>
                  {active && (
                    <Check aria-hidden="true" className="w-4 h-4 flex-shrink-0" />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default FilterDropdown;
